import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../store/useAppStore';
import { getFlatToppings } from '../../../utils/orderUtils';
import { getOutOfStock } from '../../../utils/stockUtils';

function getPartsSummary(order, config) {
  if (!order.parts?.length) return null;
  const allToppings = order.parts.map((part) => getFlatToppings(part, config));
  const common = allToppings.reduce((acc, t) => acc.filter((x) => t.includes(x)));
  const unique = allToppings.map((t) => t.filter((x) => !common.includes(x)));
  return { common, unique };
}

function ToppingsList({ items, outOfStock, toneClass = '' }) {
  const { t } = useTranslation();
  if (items.length === 0) return <p className="theme-muted">{t('ts.none')}</p>;
  return (
    <ul className="list-disc space-y-1 pl-4">
      {items.map((topping) => (
        <li
          key={topping}
          className={`text-base ${toneClass} ${outOfStock.has(topping) ? 'line-through opacity-60' : ''}`}
        >
          {topping}
        </li>
      ))}
    </ul>
  );
}

function PartCell({ part, unique, outOfStock, className }) {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <p className="mb-1.5 text-sm font-semibold">{part.customerName}</p>
      {unique.length > 0 ? (
        <ul className="list-disc space-y-1 pl-4">
          {unique.map((topping) => (
            <li key={topping} className={`text-base ${outOfStock.has(topping) ? 'line-through opacity-60' : ''}`}>
              {topping}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm theme-muted">{t('ts.noUnique')}</p>
      )}
    </div>
  );
}

function MultiPartToppings({ order, config, outOfStock }) {
  const { t } = useTranslation();
  const summary = getPartsSummary(order, config);
  if (!summary) return null;

  const twoParts = order.parts.length === 2;

  return (
    <div className="space-y-3">
      <p className="font-semibold">{t('ts.toPlace')}</p>

      {summary.common.length > 0 && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
          <p className="mb-1.5 text-sm font-semibold theme-accent">{t('ts.allParts')}</p>
          <ToppingsList items={summary.common} outOfStock={outOfStock} toneClass="theme-accent" />
        </div>
      )}

      {twoParts ? (
        <div className="grid grid-cols-2 overflow-hidden rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-surface)]">
          {order.parts.map((part, i) => (
            <PartCell
              key={i}
              part={part}
              unique={summary.unique[i]}
              outOfStock={outOfStock}
              className={`px-3 py-3 ${i === 1 ? 'border-l border-[color:var(--app-border)]' : ''}`}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {order.parts.map((part, i) => (
            <PartCell
              key={i}
              part={part}
              unique={summary.unique[i]}
              outOfStock={outOfStock}
              className="rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-surface)] px-3 py-3"
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PreparingToppings({ order, config, outOfStock }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="mb-1.5 font-semibold">{t('ts.toPlace')}</p>
      <ToppingsList items={getFlatToppings(order, config)} outOfStock={outOfStock} />
    </div>
  );
}

function CompactToppings({ order, config, outOfStock }) {
  const { t } = useTranslation();
  const flat = getFlatToppings(order, config);
  return (
    <p className="text-sm">
      <span className="theme-muted">{t('ts.toppings')}</span>
      {flat.length > 0 ? (
        <span>
          {flat.map((topping, index) => (
            <span key={topping}>
              {index > 0 ? ', ' : ''}
              <span className={outOfStock.has(topping) ? 'line-through opacity-60' : ''}>{topping}</span>
            </span>
          ))}
        </span>
      ) : (
        t('ts.none')
      )}
    </p>
  );
}

function ToppingsSection({ order }) {
  const { t } = useTranslation();
  const config = useAppStore((state) => state.config);
  const outOfStock = new Set(getOutOfStock(config));
  return (
    <div className="rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-card)] px-4 py-3">
      {order.state === 'preparing' && order.parts ? (
        <MultiPartToppings order={order} config={config} outOfStock={outOfStock} />
      ) : order.state === 'preparing' ? (
        <PreparingToppings order={order} config={config} outOfStock={outOfStock} />
      ) : (
        <CompactToppings order={order} config={config} outOfStock={outOfStock} />
      )}

      {order.comment && (
        <p className="mt-2 text-sm theme-muted">
          <span className="font-medium">{t('ts.comment')}</span> {order.comment}
        </p>
      )}
      {order.offer && (
        <p className="mt-1 text-sm theme-muted">
          <span className="font-medium">{t('ts.offer')}</span> {order.offer}
        </p>
      )}
    </div>
  );
}

export default ToppingsSection;
